import React, { createContext, useState, useContext, useEffect } from "react";
import logger from "../lib/logger";
import { useBackendHealth, BACKEND_STATUS } from "./BackendHealthContext";

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // Full user profile with preferences
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Get backend health status
  const { backendStatus, isReady } = useBackendHealth();

  // Initialize auth state from Scalekit cookies
  // Only run when backend is ready
  useEffect(() => {
    // Don't initialize auth until backend is ready
    if (backendStatus !== BACKEND_STATUS.READY) {
      logger.info(
        "Waiting for backend to be ready before initializing auth...",
      );
      return;
    }

    const initializeAuth = async () => {
      try {
        const BACKEND_URL =
          process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

        // Try Scalekit authentication (using HttpOnly cookies)
        const scalekitResponse = await fetch(`${BACKEND_URL}/api/scalekit/me`, {
          credentials: "include", // Include cookies
        });

        if (scalekitResponse.ok) {
          const userData = await scalekitResponse.json();
          setUser(userData);
          setIsAuthenticated(true);

          // Fetch full user profile with preferences
          try {
            const profileResponse = await fetch(`${BACKEND_URL}/api/users/me`, {
              credentials: "include",
            });
            if (profileResponse.ok) {
              const profileData = await profileResponse.json();
              setUserProfile(profileData);
              logger.info("User profile loaded", {
                onboarding_completed: profileData.onboarding_completed,
              });
            }
          } catch (profileError) {
            logger.error("Error fetching user profile:", profileError);
            // Continue even if profile fetch fails
          }

          logger.info("Auth initialized successfully");
        } else {
          logger.info("No active Scalekit session found");
        }
      } catch (error) {
        logger.error("Error initializing auth:", error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [backendStatus]);

  // Login with Scalekit (redirect to hosted page)
  const loginWithScalekit = () => {
    const BACKEND_URL =
      process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
    // Redirect to Scalekit login endpoint (backend will redirect to Scalekit hosted page)
    window.location.href = `${BACKEND_URL}/api/scalekit/login`;
  };

  // Logout from Scalekit
  const logout = async () => {
    try {
      const BACKEND_URL =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
      await fetch(`${BACKEND_URL}/api/scalekit/logout`, {
        method: "POST",
        credentials: "include", // Include cookies
      });
    } catch (error) {
      logger.error("Scalekit logout error:", error);
    } finally {
      // Clear local state
      setUser(null);
      setUserProfile(null);
      setIsAuthenticated(false);
    }
  };

  // Refresh user profile (after onboarding, preference changes, etc.)
  const refreshUserProfile = async () => {
    try {
      const BACKEND_URL =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
      const profileResponse = await fetch(`${BACKEND_URL}/api/users/me`, {
        credentials: "include",
      });
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setUserProfile(profileData);
        logger.info("User profile refreshed");
        return profileData;
      }
    } catch (error) {
      logger.error("Error refreshing user profile:", error);
    }
    return null;
  };

  const value = {
    user,
    userProfile,
    isAuthenticated,
    isLoading,
    // Scalekit auth functions (renamed for simplicity)
    login: loginWithScalekit,
    logout,
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
